import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Package } from "lucide-react";
import { NewsletterProductPicker } from "./NewsletterProductPicker";
import { NewsletterDraftsList } from "./NewsletterDraftsList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin Newsletter tab — extracted from AdminApp.jsx Dashboard component.
 * Props are intentionally explicit (no context) to keep the parent's state
 * the single source of truth and the extraction zero-regression.
 */
export const NewsletterTab = ({
  newsletterSubscriptions,
  newsletterStats,
  loading,
  setLoading,
  setError,
  setSuccess,
  fetchNewsletterSubscriptions,
}) => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [previewHtml, setPreviewHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [drafts, setDrafts] = useState([]);

  const loadDrafts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/newsletter/drafts`);
      setDrafts(r.data.drafts || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const resetForm = () => { setSubject(""); setMessage(""); setSelectedProducts([]); setDraftId(null); };

  const saveDraft = async () => {
    if (!subject && !message) { setError("Scrivi almeno oggetto o messaggio"); return; }
    try {
      const r = await axios.post(`${API}/newsletter/drafts`, { id: draftId, subject, message, article_ids: selectedProducts });
      setDraftId(r.data.draft.id);
      setSuccess("Newsletter salvata: la trovi qui sotto per programmarla o riusarla");
      await loadDrafts();
    } catch (e) {
      setError(e.response?.data?.detail || "Salvataggio fallito");
    }
  };

  const loadDraft = (d) => {
    setSubject(d.subject || ""); setMessage(d.message || ""); setSelectedProducts(d.article_ids || []); setDraftId(d.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openPreview = async () => {
    if (!message) { setError("Scrivi prima il messaggio"); return; }
    try {
      const r = await axios.post(`${API}/newsletter/broadcast/preview`, { subject, message, article_ids: selectedProducts });
      setPreviewHtml(r.data.html);
    } catch (e) {
      setError(e.response?.data?.detail || "Anteprima non disponibile");
    }
  };

  return (
    <>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white">Gestione Newsletter</h2>
        <p className="text-gray-400">Totale iscritti: {newsletterSubscriptions.length}</p>
      </div>

      {/* Newsletter Stats Cards */}
      {newsletterStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="newsletter-stats">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{newsletterStats.total}</p>
              <p className="text-xs text-gray-500">Totale Iscritti</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{newsletterStats.active}</p>
              <p className="text-xs text-gray-500">Attivi</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-500">{newsletterStats.inactive}</p>
              <p className="text-xs text-gray-500">Disiscritti</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-500">{newsletterStats.new_this_week}</p>
              <p className="text-xs text-gray-500">Nuovi (7gg)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{newsletterStats.new_this_month}</p>
              <p className="text-xs text-gray-500">Nuovi (30gg)</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Send Newsletter Form */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">📤 Invia Newsletter</h3>
        <p className="text-sm text-gray-600 mb-4">
          La newsletter verrà inviata a tutti gli iscritti ({newsletterSubscriptions.length} persone) + te (admin)
        </p>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!subject || !message) {
              setError("Compila tutti i campi");
              return;
            }

            try {
              setLoading(true);
              const response = await axios.post(`${API}/newsletter/broadcast`, {
                subject,
                message,
                article_ids: selectedProducts,
              });
              setSuccess(`Newsletter inviata con successo a ${response.data.sent} destinatari!`);
              if (draftId) await axios.post(`${API}/newsletter/drafts`, { id: draftId, subject, message, article_ids: selectedProducts }).catch(() => {});
              resetForm();
              loadDrafts();
            } catch (error) {
              setError(error.response?.data?.detail || "Errore nell'invio della newsletter");
            } finally {
              setLoading(false);
            }
          }}
        >
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Oggetto Email</label>
            <input
              type="text"
              name="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="newsletter-subject-input"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="es: 🆕 Nuovi arrivi nel catalogo!"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Messaggio</label>
            <textarea
              name="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="newsletter-message-input"
              rows="8"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Scrivi qui il contenuto della newsletter..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Il messaggio verrà formattato automaticamente nell'email
            </p>
          </div>

          <NewsletterProductPicker selected={selectedProducts} onChange={setSelectedProducts} />

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
              data-testid="newsletter-send-btn"
            >
              {loading ? "Invio in corso..." : `📧 Invia a ${newsletterSubscriptions.length + 1} destinatari`}
            </button>
            <button
              type="button"
              onClick={openPreview}
              className="bg-gray-100 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-200 font-medium border"
              data-testid="newsletter-preview-btn"
            >
              👁️ Anteprima
            </button>
            <button
              type="button"
              onClick={saveDraft}
              className="bg-pink-50 text-pink-700 px-6 py-3 rounded-lg hover:bg-pink-100 font-medium border border-pink-200"
              data-testid="newsletter-save-draft-btn"
            >
              💾 {draftId ? "Aggiorna bozza" : "Salva bozza"}
            </button>
            {draftId && (
              <button type="button" onClick={resetForm} className="text-sm text-gray-500 underline" data-testid="newsletter-new-btn">Nuova newsletter</button>
            )}
          </div>
        </form>
        {previewHtml && (
          <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={() => setPreviewHtml("")} data-testid="newsletter-preview-modal">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center px-4 py-2 border-b">
                <span className="font-semibold text-gray-800 text-sm">Anteprima email</span>
                <button onClick={() => setPreviewHtml("")} className="text-gray-500 hover:text-gray-900 text-xl leading-none" data-testid="newsletter-preview-close">×</button>
              </div>
              <iframe title="anteprima" srcDoc={previewHtml} className="flex-1 w-full min-h-[70vh] bg-white" data-testid="newsletter-preview-frame" />
            </div>
          </div>
        )}
      </div>

      <NewsletterDraftsList drafts={drafts} onLoad={loadDraft} onChanged={loadDrafts} setError={setError} setSuccess={setSuccess} />

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Caricamento...</p>
        </div>
      ) : newsletterSubscriptions.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Package className="w-24 h-24 mx-auto" />
          </div>
          <h3 className="text-xl font-medium text-gray-200 mb-2">Nessun iscritto</h3>
          <p className="text-gray-600 mb-6">Le iscrizioni alla newsletter appariranno qui!</p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data Iscrizione</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {newsletterSubscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{sub.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{sub.name || "-"}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(sub.subscribed_at).toLocaleDateString("it-IT")}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={sub.is_active ? "default" : "secondary"}>
                          {sub.is_active ? "Attivo" : "Inattivo"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={async () => {
                            if (window.confirm(`Sei sicuro di voler eliminare l'iscrizione di ${sub.email}?`)) {
                              try {
                                const token = sessionStorage.getItem("access_token");
                                await axios.delete(
                                  `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscriptions/${encodeURIComponent(sub.email)}`,
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                setSuccess("Iscrizione eliminata con successo!");
                                fetchNewsletterSubscriptions();
                                setTimeout(() => setSuccess(""), 3000);
                              } catch (err) {
                                setError("Errore nell'eliminazione");
                                setTimeout(() => setError(""), 3000);
                              }
                            }
                          }}
                        >
                          🗑️ Elimina
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};
